import React, {useEffect, useRef} from 'react'
import {ObjectInputProps, set} from 'sanity'

// Generate unique key for array items
const generateKey = () => `key-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

interface Product {
  _key?: string
  sku: string
  name: string
  id: string | number
  productType: string
  customLabel?: string
}

interface ProductCombination {
  _key?: string
  fieldLabel?: string
  products?: Product[]
  [key: string]: unknown
}

/**
 * Wrapper component for productCombination objects.
 * Automatically fixes missing _key properties on nested products array
 * BEFORE Sanity's array validation runs.
 */
const ProductCombinationInput = (props: ObjectInputProps<ProductCombination>) => {
  const {value, onChange, renderDefault} = props
  const hasFixedKeys = useRef(false)

  useEffect(() => {
    // Only run once per mount to avoid infinite loops
    if (hasFixedKeys.current) return

    if (value?.products && Array.isArray(value.products)) {
      const hasMissingKeys = value.products.some((product) => !product._key)

      if (hasMissingKeys) {
        hasFixedKeys.current = true

        const fixedProducts = value.products.map((product) => ({
          ...product,
          _key: product._key || generateKey(),
        }))

        // Update the entire object with fixed products
        onChange(
          set({
            ...value,
            products: fixedProducts,
          }),
        )
      }
    }
  }, [value, onChange])

  // Render the default Sanity object input
  return renderDefault(props)
}

export default ProductCombinationInput
